import {
  addSoftwareZoneDraft,
  deleteZone,
  renameZone,
  setZoneType
} from "../zone-geometry";
import { zoneGeometryMessage } from "../i18n/zone-geometry";
import type { Messages } from "../i18n/types";
import type { WebDeviceConfig, WebZone, WebZoneType } from "../types";

type StatusTone = "ok" | "warn" | "error";

interface ZoneEditorOptions {
  getConfig: () => WebDeviceConfig | null;
  getZones: () => WebZone[];
  getCalibrationZones: () => WebZone[];
  getMessages: () => Messages;
  updateConfig: (mutator: (current: WebDeviceConfig) => WebDeviceConfig) => void;
  setStatus: (message: string, tone: StatusTone) => void;
  onSelect: (zoneId: string, resetPoint: boolean, render: boolean) => void;
  canUseDetectionType?: () => boolean;
  onDetectionOwnerRequired?: () => void;
}

export function createZoneEditor({
  getConfig,
  getZones,
  getCalibrationZones,
  getMessages,
  updateConfig,
  setStatus,
  onSelect,
  canUseDetectionType = () => true,
  onDetectionOwnerRequired = () => undefined
}: ZoneEditorOptions) {
  let selectedZoneId = $state("");

  const selectedZone = $derived(getZones().find((zone) => zone.id === selectedZoneId) ?? null);
  const selectedCalibrationZone = $derived(
    getCalibrationZones().find((zone) => zone.id === selectedZoneId) ?? null
  );

  function selectZone(zoneId: string, resetPoint = true, render = true): void {
    selectedZoneId = zoneId;
    onSelect(zoneId, resetPoint, render);
  }

  function selectFirstAvailable(): void {
    selectZone(getZones()[0]?.id || getCalibrationZones()[0]?.id || "");
  }

  function addZone(): void {
    const config = getConfig();
    if (!config) return;
    const result = addSoftwareZoneDraft(config, getMessages().zones);
    if (!result.changed) {
      const message = zoneGeometryMessage(getMessages(), result.messageCode, result.messageParams);
      if (message) setStatus(message, "warn");
      return;
    }
    updateConfig(() => result.config);
    selectZone(result.selectedZoneId ?? "");
  }

  function deleteSelected(): void {
    const config = getConfig();
    if (!config || !selectedZoneId) return;
    if (selectedZone?.type === "detection" && !canUseDetectionType()) {
      onDetectionOwnerRequired();
      return;
    }
    const result = deleteZone(config, selectedZoneId);
    if (!result.changed) return;
    updateConfig(() => result.config);
    selectZone(result.selectedZoneId ?? "");
  }

  function setSelectedZoneType(type: WebZoneType): void {
    if (!selectedZone) return;
    if ((!selectedZone.placeholder && selectedZone.type === "detection" && !canUseDetectionType()) ||
        (type === "detection" && !canUseDetectionType())) {
      onDetectionOwnerRequired();
      return;
    }
    updateConfig((current) => setZoneType(current, selectedZone.id, type).config);
  }

  function setSelectedZoneName(name: string): void {
    if (!selectedZone) return;
    if (selectedZone.type === "detection" && !selectedZone.placeholder && !canUseDetectionType()) {
      onDetectionOwnerRequired();
      return;
    }
    updateConfig((current) => renameZone(current, selectedZone.id, name).config);
  }

  return {
    get selectedZoneId() {
      return selectedZoneId;
    },
    get selectedZone() {
      return selectedZone;
    },
    get selectedCalibrationZone() {
      return selectedCalibrationZone;
    },
    addZone,
    deleteSelected,
    selectFirstAvailable,
    selectZone,
    setSelectedZoneName,
    setSelectedZoneType
  };
}
